import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import Document from "../components/document/document";
import { useDispatch } from "react-redux";
import { useActions } from "../services/hooks/useActions";
import { useAppSelector } from "../services/hooks";
import {
  therapistWithUsersSelector,
  usersWithOutTherapistSelector,
  userSelector,
} from "../services/selectors/user.selectors";
import {
  callStateSelector,
  cdrDataSelector,
  currentCallSelector,
} from "../services/selectors/callCenter.selectors";
import { isAuthSelector } from "../services/selectors/auth.selectors";
import { clearCall } from "../services/slices/callCenter.slice";
import { TCallDocumentContext } from "../types/callCenter.types";
import type {
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../services/types/user.types";
import { resolveInquiryDocumentForIncomingCall } from "../utils/resolveInquiryDocumentForCall";

export type InquiryDocumentPanelContextValue = {
  openInquiryDocument: (doc: TTherapistWithUsers | TUsersWithOutTherapist) => void;
  /** Opens the referral card for the current MVoice session (during or after ringing). */
  openInquiryDocumentForActiveCall: () => boolean;
  /** Opens the inquiry drawer with post-call CDR section after hang-up. */
  openPostCallInquiryDocument: () => boolean;
};

const InquiryDocumentPanelContext =
  createContext<InquiryDocumentPanelContextValue | null>(null);

export function InquiryDocumentPanelProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}`;
  const prevRouteKeyRef = useRef(routeKey);
  const postCallOpenedForCallIdRef = useRef<string | null>(null);
  /** Route where the panel was opened — panel is only visible while this matches current route. */
  const openedOnRouteKeyRef = useRef<string | null>(null);
  /** Blocks post-call auto-open after the user navigates away. */
  const navigationDismissedRef = useRef(false);
  const { setCurrentInquiry, setCurrentTreatmentPlan } = useActions();
  const isAuth = useAppSelector(isAuthSelector);
  const therapist = useAppSelector(userSelector);
  const therapistWithUsers = useAppSelector(therapistWithUsersSelector);
  const usersWithOutTherapist = useAppSelector(usersWithOutTherapistSelector);
  const callState = useAppSelector(callStateSelector);
  const currentCall = useAppSelector(currentCallSelector);
  const cdrData = useAppSelector(cdrDataSelector);

  const [openedOnRouteKey, setOpenedOnRouteKey] = useState<string | null>(null);
  const [openDocument, setOpenDocument] = useState(false);
  const [documentToShow, setDocumentToShow] = useState<
    TTherapistWithUsers | TUsersWithOutTherapist | null
  >(null);
  const [callDocumentData, setCallDocumentData] = useState<TCallDocumentContext | null>(
    null
  );

  const isPanelVisible =
    openDocument && openedOnRouteKey !== null && openedOnRouteKey === routeKey;

  const hidePanel = useCallback(() => {
    navigationDismissedRef.current = true;
    openedOnRouteKeyRef.current = null;
    setOpenedOnRouteKey(null);
    postCallOpenedForCallIdRef.current = null;
    setOpenDocument(false);
    setDocumentToShow(null);
    setCallDocumentData(null);
    setCurrentInquiry(null);
  }, [setCurrentInquiry]);

  useEffect(() => {
    if (!isAuth) {
      navigationDismissedRef.current = false;
      openedOnRouteKeyRef.current = null;
      setOpenedOnRouteKey(null);
      postCallOpenedForCallIdRef.current = null;
      setOpenDocument(false);
      setDocumentToShow(null);
      setCallDocumentData(null);
      setCurrentInquiry(null);
      setCurrentTreatmentPlan(null);
    }
  }, [isAuth, setCurrentInquiry, setCurrentTreatmentPlan]);

  useEffect(() => {
    if (callState === "idle" || callState === "ringing") {
      postCallOpenedForCallIdRef.current = null;
      if (callState === "idle") {
        navigationDismissedRef.current = false;
      }
    }
    // Answer flow may navigate to /main (hidePanel sets dismissed); allow post-call doc.
    if (callState === "active") {
      navigationDismissedRef.current = false;
    }
  }, [callState]);

  const openPostCallInquiryDocument = useCallback(() => {
    if (navigationDismissedRef.current) {
      return false;
    }
    if (openedOnRouteKeyRef.current !== null && openedOnRouteKeyRef.current !== routeKey) {
      return false;
    }
    if (callState !== "ended" || !cdrData || !currentCall) {
      return false;
    }
    if (cdrData.callId !== currentCall.callId) {
      return false;
    }

    const callDocument = resolveInquiryDocumentForIncomingCall(
      currentCall,
      therapist,
      therapistWithUsers,
      usersWithOutTherapist
    );

    postCallOpenedForCallIdRef.current = cdrData.callId;
    openedOnRouteKeyRef.current = routeKey;
    setOpenedOnRouteKey(routeKey);
    setDocumentToShow(callDocument);
    setCallDocumentData({
      cdr: cdrData,
      isUnidentified: !currentCall.isIdentified,
      callerPhone: currentCall.cli,
    });
    setOpenDocument(true);
    setCurrentInquiry(callDocument.num);
    setCurrentTreatmentPlan(null);
    return true;
  }, [
    callState,
    cdrData,
    currentCall,
    routeKey,
    therapist,
    therapistWithUsers,
    usersWithOutTherapist,
    setCurrentInquiry,
    setCurrentTreatmentPlan,
  ]);

  /** Close panel on route change — never clear MVoice call/CDR in Redux here. */
  useLayoutEffect(() => {
    if (prevRouteKeyRef.current === routeKey) {
      return;
    }
    prevRouteKeyRef.current = routeKey;
    hidePanel();
  }, [routeKey, hidePanel]);

  /** Auto-open post-call document once when a call ends on the current screen. */
  useLayoutEffect(() => {
    if (navigationDismissedRef.current) {
      return;
    }
    if (callState !== "ended" || !cdrData || !currentCall) {
      return;
    }
    if (cdrData.callId !== currentCall.callId) {
      return;
    }
    if (postCallOpenedForCallIdRef.current === cdrData.callId) {
      return;
    }
    openPostCallInquiryDocument();
  }, [callState, cdrData, currentCall, openPostCallInquiryDocument]);

  /** Refresh open row when main-page caches update — keep previous row if lookup is briefly stale. */
  useEffect(() => {
    if (navigationDismissedRef.current) {
      return;
    }
    if (openedOnRouteKeyRef.current !== routeKey) {
      return;
    }

    const findFresh = (num: string | number) =>
      therapistWithUsers?.find((item) => String(item.num) === String(num)) ||
      usersWithOutTherapist?.find((item) => String(item.num) === String(num));

    setDocumentToShow((prev) => {
      if (!prev || callDocumentData) return prev;
      const updated = findFresh(prev.num);
      return updated ?? prev;
    });
  }, [callDocumentData, routeKey, therapistWithUsers, usersWithOutTherapist]);

  const handleCloseDocument = useCallback(() => {
    openedOnRouteKeyRef.current = null;
    setOpenedOnRouteKey(null);
    setOpenDocument(false);
    setDocumentToShow(null);
    setCurrentInquiry(null);
    postCallOpenedForCallIdRef.current = null;
    if (callDocumentData) {
      setCallDocumentData(null);
      dispatch(clearCall());
    }
  }, [callDocumentData, dispatch, setCurrentInquiry]);

  const openInquiryDocument = useCallback(
    (doc: TTherapistWithUsers | TUsersWithOutTherapist) => {
      navigationDismissedRef.current = false;
      openedOnRouteKeyRef.current = routeKey;
      setOpenedOnRouteKey(routeKey);
      setCallDocumentData(null);
      setDocumentToShow(doc);
      setOpenDocument(true);
      setCurrentInquiry(doc.num);
      setCurrentTreatmentPlan(null);
    },
    [routeKey, setCurrentInquiry, setCurrentTreatmentPlan]
  );

  const openInquiryDocumentForActiveCall = useCallback(() => {
    if (!currentCall) {
      return false;
    }
    navigationDismissedRef.current = false;
    openedOnRouteKeyRef.current = routeKey;
    setOpenedOnRouteKey(routeKey);
    const doc = resolveInquiryDocumentForIncomingCall(
      currentCall,
      therapist,
      therapistWithUsers,
      usersWithOutTherapist
    );
    setCallDocumentData(null);
    setDocumentToShow(doc);
    setOpenDocument(true);
    setCurrentInquiry(doc.num);
    setCurrentTreatmentPlan(null);
    return true;
  }, [
    currentCall,
    routeKey,
    therapist,
    therapistWithUsers,
    usersWithOutTherapist,
    setCurrentInquiry,
    setCurrentTreatmentPlan,
  ]);

  const value = useMemo(
    (): InquiryDocumentPanelContextValue => ({
      openInquiryDocument,
      openInquiryDocumentForActiveCall,
      openPostCallInquiryDocument,
    }),
    [openInquiryDocument, openInquiryDocumentForActiveCall, openPostCallInquiryDocument]
  );

  return (
    <InquiryDocumentPanelContext.Provider value={value}>
      {documentToShow && isPanelVisible && (
        <Document
          document={documentToShow}
          openDocument={isPanelVisible}
          onClose={handleCloseDocument}
          callData={callDocumentData}
        />
      )}
      {children}
    </InquiryDocumentPanelContext.Provider>
  );
}

export function useInquiryDocumentPanel(): InquiryDocumentPanelContextValue {
  const ctx = useContext(InquiryDocumentPanelContext);
  if (!ctx) {
    throw new Error(
      "useInquiryDocumentPanel must be used within InquiryDocumentPanelProvider"
    );
  }
  return ctx;
}
